import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import FeedBuilderContainer from './FeedBuilderContainer';
import ConnectedLocationSearchAutocomplete from './ConnectedLocationSearchAutocomplete';

import { fetchEventsForTab } from '../redux/events/actions';
import { EventClient, EventClientProvider } from '@eb/event-renderer';
import {
    ALL_TAB,
    FEED_TABS,
    FOR_YOU,
    ONLINE_TAB,
} from '../constants/tabConfig';
import {
    EVENT_SEARCH_PROPTYPES,
    LOCATION_PROP_TYPE,
} from '../constants/propTypes';
import { BASE_SEASONAL_CONTENT_PROPTYPES } from '../constants/seasonal';
import { inyectSeasonalTab } from '../utils/seasonal';
import { scrollIt } from '../utils/random';

import gettext from '@eb/gettext';

import StickyTabs from '../components/StickyTabs';

import './BrowseContainer.scss';
import { Icon } from '@eb/eds-icon';
import { ChevronDownChunky } from '@eb/eds-iconography';

const eventClient = new EventClient();
const Chevron = (
    <Icon type={<ChevronDownChunky />} color="ui-blue--hover" size="medium" />
);

export const ONLINE_EVENTS_INPUT_COPY = gettext('Popular ');

const LOCATION_AUTOCOMPLETE_PROPS = {
    prefix: Chevron,
    borderType: 'content',
    id: 'locationPicker',
    name: 'locationPicker',
    hideOutline: true,
    isLimitedSearch: true,
    preventInputValuePrefill: true,
    shouldRemoveConflictiveAria: true,
};

const shouldIncludeCurrentTab = ({
    shouldShowForYouTab,
    tabKey,
    tabsConfig,
    location,
}) => {
    if (!tabsConfig[tabKey]) {
        return false;
    }

    if (tabKey === FOR_YOU && !shouldShowForYouTab) {
        return false;
    }

    if (tabKey === ONLINE_TAB && (location.isOnline || !location.placeId)) {
        return false;
    }

    return true;
};

const getBrowseTabs = (shouldShowForYouTab, seasonalContent, location) => {
    const tabsConfig = inyectSeasonalTab(seasonalContent);
    const tabs = [];

    FEED_TABS.forEach((tabKey, index) => {
        const shouldAddTab = shouldIncludeCurrentTab({
            shouldShowForYouTab,
            tabKey,
            tabsConfig,
            location,
        });

        if (shouldAddTab) {
            const { name, eventSearch, follow, feedConfig } = tabsConfig[
                tabKey
            ];

            tabs.push({
                tabKey,
                value: index,
                displayName: name,
                content: (
                    <FeedBuilderContainer
                        feedConfig={feedConfig}
                        eventSearch={eventSearch}
                        follow={follow}
                    />
                ),
            });
        }
    });

    return tabs;
};

const LocationComponent = () => {
    const copy = gettext('Popular in ');

    return (
        <div className="browse__location eds-l-pad-top-4">
            <div className="browse__location-text">{copy} </div>
            <ConnectedLocationSearchAutocomplete
                autocompleteProps={LOCATION_AUTOCOMPLETE_PROPS}
                shouldDispatchSearchActions={true}
            />
        </div>
    );
};

const handleTabLoad = (tabKey, { fetchEventsForTab, isFeLoading }) => {
    /**
     * On first load we do not need to fetch new events ONLY for the all_tab.
     * The tab is loaded before the `fe` has finished fetching so we use that
     * variable to know if we are in the all tab to prevent fetching.
     */
    if ((!isFeLoading && tabKey === ALL_TAB) || tabKey !== ALL_TAB) {
        fetchEventsForTab({ tabKey, shouldUpdateContent: true });
    }
};

const handleTabChange = (tabKey, { fetchEventsForTab }) => {
    scrollIt(document.querySelector('.browse-section').offsetTop + 70);

    fetchEventsForTab({ tabKey, shouldUpdateContent: true });
};

const Browse = (props) => {
    const {
        shouldShowForYouTab,
        tabKey,
        location,
        seasonalContent,
        fetchEventsForTab,
        isFeLoading,
    } = props;
    const items = getBrowseTabs(shouldShowForYouTab, seasonalContent, location);

    return (
        <EventClientProvider client={eventClient}>
            <div className="browse-section" id="browse-section">
                <LocationComponent location={location} />

                <StickyTabs
                    items={items}
                    onTabChange={(tabKey) =>
                        handleTabChange(tabKey, {
                            fetchEventsForTab,
                            isFeLoading,
                        })
                    }
                    onTabLoad={(tabKey) =>
                        handleTabLoad(tabKey, {
                            fetchEventsForTab,
                            isFeLoading,
                        })
                    }
                    initialSelectedTab={tabKey}
                    location={location}
                />
            </div>
        </EventClientProvider>
    );
};

Browse.propTypes = {
    fetchEventsForTab: PropTypes.func,
    eventSearch: EVENT_SEARCH_PROPTYPES,
    isAuthenticated: PropTypes.bool,
    tabKey: PropTypes.string,
    shouldShowForYouTab: PropTypes.bool,
    location: LOCATION_PROP_TYPE,
    seasonalContent: PropTypes.arrayOf(
        PropTypes.shape(BASE_SEASONAL_CONTENT_PROPTYPES),
    ),
    isFeLoading: PropTypes.bool,
};

const _mapStateToProps = (state) => ({
    isAuthenticated: state.user.isAuthenticated,
    tabKey: state.content.browseState.tabKey,
    shouldShowForYouTab: state.app.featureFlags.launchForYouTab,
    isFeLoading: state.app.loadingState.isFeLoading,
    location: state.location,
    seasonalContent: state.content.seasonal,
});

const _mapDispatchToProps = {
    fetchEventsForTab,
};

export default connect(_mapStateToProps, _mapDispatchToProps)(Browse);
